/**
 * hiring.js - Dynamic job loader, filtering, and detail drawer management for Forsyt Energy Careers
 */

const FALLBACK_JOBS = [];

let allJobs = [];
let activeCategory = "all";
let jobsLoaded = false;

// UI chrome only; job-posting content from data/jobs.json stays as written.
const UI_STRINGS = {
  en: {
    empty: "No active openings at this time.",
    viewDetails: "View Details",
    linkedInTitle: "View offer on LinkedIn",
    linkedInLabel: "LinkedIn Offer",
    responsibilities: "WHAT YOU WILL DO",
    requirements: "WHAT WE ARE LOOKING FOR",
    benefits: "WHAT WE OFFER",
    howToApply: "HOW TO APPLY / POUR CANDIDATER",
    apply: "Apply for this position",
    writeTo: "Or write to",
  },
  fr: {
    empty: "Aucun poste ouvert pour le moment.",
    viewDetails: "Voir le détail",
    linkedInTitle: "Voir l'offre sur LinkedIn",
    linkedInLabel: "Offre LinkedIn",
    responsibilities: "VOS MISSIONS",
    requirements: "VOTRE PROFIL",
    benefits: "CE QUE NOUS OFFRONS",
    howToApply: "COMMENT POSTULER",
    apply: "Postuler à ce poste",
    writeTo: "Ou écrivez à",
  },
  de: {
    empty: "Derzeit sind keine Stellen ausgeschrieben.",
    viewDetails: "Details ansehen",
    linkedInTitle: "Stellenanzeige auf LinkedIn ansehen",
    linkedInLabel: "LinkedIn-Stellenanzeige",
    responsibilities: "IHRE AUFGABEN",
    requirements: "IHR PROFIL",
    benefits: "WAS WIR BIETEN",
    howToApply: "SO BEWERBEN SIE SICH",
    apply: "Auf diese Stelle bewerben",
    writeTo: "Oder schreiben Sie an",
  },
};

function t(key) {
  const lang = window.forsytLang || "en";
  const table = UI_STRINGS[lang] || UI_STRINGS.en;
  return key in table ? table[key] : UI_STRINGS.en[key];
}

document.addEventListener("DOMContentLoaded", () => {
  initJobs();
});

// main.js re-applies the language on toggle; re-render the job cards so their
// UI chrome follows, keeping open drawers open.
document.addEventListener("forsyt:langchange", () => {
  if (jobsLoaded) renderJobs();
});

async function initJobs() {
  const container = document.getElementById("jobsContainer");
  if (!container) return;

  try {
    const res = await fetch("data/jobs.json");
    if (res.ok) {
      const data = await res.json();
      allJobs = (data.jobs || []).filter(j => j.status === "active");
    } else {
      allJobs = FALLBACK_JOBS;
    }
  } catch (err) {
    console.warn("Using fallback jobs dataset due to fetch error:", err);
    allJobs = FALLBACK_JOBS;
  }

  jobsLoaded = true;
  renderJobs();
}

function renderJobs() {
  const container = document.getElementById("jobsContainer");
  if (!container) return;

  if (allJobs.length === 0) {
    container.innerHTML = `
      <div class="jobs-empty-state">
        <p class="jobs-empty-text">${escapeHtml(t("empty"))}</p>
      </div>
    `;
    return;
  }

  const expandedIds = Array.from(
    container.querySelectorAll(".job-card.expanded"),
  ).map(card => card.id);

  container.innerHTML = allJobs.map(job => createJobCardHTML(job)).join("");

  expandedIds.forEach(id => {
    const card = document.getElementById(id);
    if (!card) return;
    const drawer = card.querySelector(".job-card-details");
    const toggleIcon = card.querySelector(".job-toggle-icon");
    card.classList.add("expanded");
    if (drawer) drawer.style.maxHeight = drawer.scrollHeight + "px";
    if (toggleIcon) toggleIcon.textContent = "−";
  });

  // Attach toggle listeners for job detail drawers
  container.querySelectorAll(".job-card-header").forEach(header => {
    header.addEventListener("click", () => {
      const card = header.closest(".job-card");
      const drawer = card.querySelector(".job-card-details");
      const toggleIcon = card.querySelector(".job-toggle-icon");
      const isExpanded = card.classList.contains("expanded");

      if (isExpanded) {
        card.classList.remove("expanded");
        drawer.style.maxHeight = null;
        if (toggleIcon) toggleIcon.textContent = "+";
      } else {
        card.classList.add("expanded");
        drawer.style.maxHeight = drawer.scrollHeight + "px";
        if (toggleIcon) toggleIcon.textContent = "−";
      }
    });
  });
}

function createJobCardHTML(job) {
  const subject = encodeURIComponent(`Application for ${job.title} - [Your Name]`);
  const mailtoUrl = `mailto:${job.applyEmail}?subject=${subject}`;

  const respList = (job.responsibilities || [])
    .map(item => `<li>${escapeHtml(item)}</li>`)
    .join("");

  const reqList = (job.requirements || [])
    .map(item => `<li>${escapeHtml(item)}</li>`)
    .join("");

  const benList = (job.benefits || [])
    .map(item => `<li>${escapeHtml(item)}</li>`)
    .join("");

  return `
    <article class="insights-card job-card ${job.featured ? "featured-job" : ""}" id="job-${job.id}">
      <div class="job-card-header" role="button" tabindex="0">
        <div class="job-card-main-info">
          <div class="job-badges">
            <span class="badge dept-badge">${escapeHtml(job.department)}</span>
            <span class="badge location-badge">${escapeHtml(job.location)}</span>
            <span class="badge type-badge">${escapeHtml(job.type)}</span>
          </div>
          <h3 class="insights-module-title" style="text-align: left; font-size: 1.35rem; margin-bottom: 0.5rem;">${escapeHtml(job.title)}</h3>
          <p class="job-summary">${escapeHtml(job.summary)}</p>
        </div>
        <div class="job-card-action">
          <span class="insights-link job-toggle-btn">
            ${escapeHtml(t("viewDetails"))} <span class="job-toggle-icon">+</span>
          </span>
          ${job.linkedInUrl ? `
            <a href="${escapeHtml(job.linkedInUrl)}" target="_blank" rel="noopener noreferrer" class="linkedin-icon-btn" title="${escapeHtml(t("linkedInTitle"))}" aria-label="${escapeHtml(t("linkedInLabel"))}" onclick="event.stopPropagation();">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452z"/></svg>
            </a>
          ` : ""}
        </div>
      </div>

      <div class="job-card-details">
        <div class="job-details-inner">
          <p class="job-full-desc">${escapeHtml(job.description || "")}</p>

          ${respList ? `
            <div class="job-section-block">
              <span class="insights-eyebrow">${escapeHtml(t("responsibilities"))}</span>
              <ul class="insights-text-list">${respList}</ul>
            </div>
          ` : ""}

          ${reqList ? `
            <div class="job-section-block">
              <span class="insights-eyebrow">${escapeHtml(t("requirements"))}</span>
              <ul class="insights-text-list">${reqList}</ul>
            </div>
          ` : ""}

          ${benList ? `
            <div class="job-section-block">
              <span class="insights-eyebrow">${escapeHtml(t("benefits"))}</span>
              <ul class="insights-text-list">${benList}</ul>
            </div>
          ` : ""}

          ${job.howToApply ? `
            <div class="job-section-block">
              <span class="insights-eyebrow">${escapeHtml(t("howToApply"))}</span>
              <p style="color: var(--text-light); font-size: 1rem; line-height: 1.6;">${escapeHtml(job.howToApply)}</p>
            </div>
          ` : ""}

          <div class="job-apply-footer">
            <a href="${mailtoUrl}" class="btn primary job-apply-btn">
              ${escapeHtml(t("apply"))}
            </a>
            <span class="job-direct-email">
              ${escapeHtml(t("writeTo"))} <a href="mailto:${job.applyEmail}">${job.applyEmail}</a>
            </span>
          </div>
        </div>
      </div>
    </article>
  `;
}

function escapeHtml(str) {
  if (!str) return "";
  return String(str)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}
